import type { CreateAdminAgencyDto, UpdateAdminAgencyDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminAgencyInput,
  V2UpdateAdminAgencyInput
} from '@idc-forms/schemas/api-v2/admin-agencies'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { agenciesAdminV2Service } from '../services/agencies-admin-v2.service'

/**
 * Mutaciones de agencias contra la API v2. Identifican por id público (`agc_`) y,
 * al completar, invalidan todo el árbol `['agencies']` (listado + summary +
 * detalle). El editor de página completa no existe: el alta/edición vive en el
 * side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateAgencyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminAgencyInput) =>
      await agenciesAdminV2Service.create(accessToken, body as CreateAdminAgencyDto),
    onSuccess: () => {
      toast.success('Agencia creada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.agencies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateAgencyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminAgencyInput }) =>
      await agenciesAdminV2Service.update(accessToken, id, body as UpdateAdminAgencyDto),
    onSuccess: () => {
      toast.success('Agencia actualizada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.agencies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteAgencyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await agenciesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Agencia eliminada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.agencies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
